import React from "react";
import { Link } from "react-router-dom";

const SHADOW_BLACK = "4px 4px 0 0 #0A0A0A";

const MARQUEE_TAGS = [
  "LLM INFERENCE",
  "VLLM / TENSORRT-LLM",
  "AGENTIC SYSTEMS",
  "MULTI-AGENT ORCHESTRATION",
  "BACKEND ENGINEERING",
  "DISTRIBUTED SYSTEMS",
  "KAFKA + REDIS",
  "ML SYSTEM DESIGN",
  "LORA / QLORA",
  "RLHF / DPO / GRPO",
  "EVAL & GUARDRAILS",
  "SCALABLE AI PRODUCTS",
];

const SECTION_CARDS = [
  { label: "ABOUT", to: "/about", color: "#15803D" },
  { label: "PROJECTS", to: "/projects", color: "#7E22CE" },
  { label: "EXPERIENCE", to: "/experience", color: "#BE185D" },
  { label: "EDUCATION", to: "/education", color: "#B45309" },
  { label: "BLOGS", to: "/blogs", color: "#15803D" },
  { label: "COOKBOOKS", to: "/cookbooks", color: "#15803D" },
];

function SectionCard({ label, to, external, color }) {
  const className =
    "group bg-white border-2 border-[#0A0A0A] py-6 px-4 flex items-center justify-center transition-transform duration-150 hover:-translate-y-1 hover:bg-[#FCFCF5]";
  const content = (
    <span
      className="font-pixel text-xs md:text-sm leading-none tracking-wider"
      style={{ color }}
    >
      {label}
    </span>
  );
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={className} style={{ boxShadow: SHADOW_BLACK }}>
        {content}
      </a>
    );
  }
  return (
    <Link to={to} className={className} style={{ boxShadow: SHADOW_BLACK }}>
      {content}
    </Link>
  );
}

export default function Portfolio() {
  return (
    <div data-testid="portfolio-page" className="relative bg-[#F5F5F0] min-h-screen overflow-hidden">
      <div className="portfolio-dots" aria-hidden="true" />

      {/* ───────────── HERO ───────────── */}
      <section className="relative pt-28 pb-12 px-4 md:px-8">
        <div className="max-w-6xl mx-auto flex flex-col items-center">
          <div
            className="inline-block bg-[#FBBF24] border-2 border-[#0A0A0A] px-5 py-2 mb-8"
            style={{ boxShadow: SHADOW_BLACK }}
          >
            <span className="font-pixel text-xs md:text-sm text-[#0A0A0A] tracking-widest">
              HELLO, I'M VIKRAM
            </span>
          </div>

          <div className="relative w-full flex justify-center mb-0">
            <div className="relative inline-flex flex-col items-center leading-none">
              <span
                className="font-pixel text-stroke-ink leading-[0.85] text-[10vw] md:text-[8vw] xl:text-[96px] text-[#EDEDE7] title-breathe-a relative z-0"
                style={{ letterSpacing: "-0.02em" }}
              >
                AI SYSTEMS
              </span>
              <span
                className="font-pixel text-stroke-ink leading-[0.85] text-[10vw] md:text-[8vw] xl:text-[96px] text-[#EDEDE7] title-breathe-b relative z-0 mt-1"
                style={{ letterSpacing: "-0.02em" }}
              >
                ENGINEER
              </span>
              <img
                src="/images/portfolio/avataar.png"
                alt=""
                aria-hidden="true"
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[36%] md:w-[26%] xl:w-[230px] pointer-events-none select-none z-10 avatar-breathe"
              />
            </div>
          </div>

          <div
            className="relative z-20 inline-block bg-[#DC2626] border-2 border-[#0A0A0A] px-10 md:px-16 py-5 md:py-7 mt-0 tagline-breathe"
            style={{ boxShadow: SHADOW_BLACK }}
          >
            <span className="font-pixel text-sm md:text-base text-white tracking-widest">
              BUILDING PRODUCTS THAT SCALE
            </span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 mt-3 md:mt-4">
            <Link
              to="/projects"
              className="bg-[#FBBF24] border-2 border-[#0A0A0A] px-5 py-2.5 font-pixel text-xs md:text-sm text-[#0A0A0A] tracking-widest transition-transform duration-150 hover:-translate-y-0.5"
              style={{ boxShadow: SHADOW_BLACK }}
            >
              VIEW PROJECTS
            </Link>
            <Link
              to="/contact"
              className="bg-[#FBBF24] border-2 border-[#0A0A0A] px-5 py-2.5 font-pixel text-xs md:text-sm text-[#0A0A0A] tracking-widest transition-transform duration-150 hover:-translate-y-0.5"
              style={{ boxShadow: SHADOW_BLACK }}
            >
              CONTACT
            </Link>
            <a
              href="https://drive.google.com/file/d/1c8MRr1XpnrARn_92-7aDtOqgb3TWj0SF/view?usp=share_link"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#FBBF24] border-2 border-[#0A0A0A] px-5 py-2.5 font-pixel text-xs md:text-sm text-[#0A0A0A] tracking-widest transition-transform duration-150 hover:-translate-y-0.5"
              style={{ boxShadow: SHADOW_BLACK }}
            >
              RESUME
            </a>
          </div>

        </div>
      </section>

      {/* ───────────── MARQUEE + SECTION GRID (same width container) ───────────── */}
      <section className="relative px-4 md:px-8 pb-24">
        <div className="max-w-6xl mx-auto">
          <div
            className="relative bg-[#4ADE80] border-2 border-[#0A0A0A] overflow-hidden py-3 mb-3 md:mb-4"
            style={{ boxShadow: "inset 0 -3px 0 0 #15803D" }}
            aria-hidden="true"
          >
            <div className="marquee-track">
              {[...MARQUEE_TAGS, ...MARQUEE_TAGS, ...MARQUEE_TAGS].map((tag, i) => (
                <span key={i} className="flex items-center gap-6 px-6 shrink-0">
                  <span className="font-pixel text-xs md:text-sm text-[#0A0A0A] tracking-widest whitespace-nowrap">
                    {tag}
                  </span>
                  <span className="inline-block w-1.5 h-1.5 bg-[#BE185D] rounded-full" aria-hidden="true" />
                </span>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {SECTION_CARDS.map((card) => (
              <SectionCard key={card.label} {...card} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
